import React, { useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "lib/utils";
import { Badge, Popover, PopoverTrigger, PopoverContent, Command, CommandList, CommandItem } from "campaign-builder/ui";
import { DISTRIBUTION_TIME_OPTIONS, timeSlotLabel } from "campaign-builder/state/h2hSchedule";

interface DistributionTimesSelectProps {
  /** Selected time-slot codes for this date row. */
  value: number[];
  /** Toggle a single slot on/off (add/remove a chip). */
  onToggle: (slot: number) => void;
  cypressId?: string;
}

// Per-date multi-select time picker (AC29): a bordered field showing each chosen
// slot as a removable chip, opening a checkable dropdown of the distribution time
// windows. Removing a chip removes only that slot.
export const DistributionTimesSelect = ({ value, onToggle, cypressId }: DistributionTimesSelectProps) => {
  const [open, setOpen] = useState(false);
  const selected = [...value].sort((a, b) => a - b);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-cypress-id={cypressId}
          className={cn(
            "flex min-h-9 w-full items-center gap-1.5 rounded-md border border-solid bg-background px-2 py-1 text-left text-sm",
            "focus:outline-none focus-visible:border-slate-400 dark:focus-visible:border-slate-500 focus-visible:ring-1 focus-visible:ring-slate-200 dark:focus-visible:ring-slate-700",
            open ? "border-slate-400 dark:border-slate-500" : "border-border"
          )}
        >
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
            {selected.length === 0 ? (
              <span className="text-muted-foreground">Pick distribution times</span>
            ) : (
              selected.map((slot) => (
                <Badge key={slot} variant="secondary" className="gap-1 font-normal">
                  {timeSlotLabel(slot)}
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={`Remove ${timeSlotLabel(slot)}`}
                    data-cypress-id={cypressId ? `${cypressId}-remove-${slot}` : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggle(slot);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        onToggle(slot);
                      }
                    }}
                    className="-mr-0.5 flex cursor-pointer items-center rounded-sm text-muted-foreground hover:text-foreground"
                  >
                    <X className="size-3" />
                  </span>
                </Badge>
              ))
            )}
          </span>
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="z-[1350] w-[var(--radix-popover-trigger-width)] p-0">
        <Command>
          <CommandList>
            {DISTRIBUTION_TIME_OPTIONS.map((option) => {
              const isSelected = value.includes(option.value);
              return (
                <CommandItem
                  key={option.value}
                  // cmdk filters on value text; the label is unique per slot.
                  value={option.label}
                  onSelect={() => onToggle(option.value)}
                  data-cypress-id={cypressId ? `${cypressId}-option-${option.value}` : undefined}
                >
                  <Check className={cn("mr-2 size-4", isSelected ? "opacity-100" : "opacity-0")} />
                  {option.label}
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
